import { FC, useState } from 'react'

import { Link } from '@tanstack/react-router'

import { Cluster, Link as PhoenixLink, Stack, Typography } from '@forto-dev/phoenix-ui-core'
import { Tooltip, TooltipContent, TooltipTrigger } from '@forto-dev/phoenix-ui-core/common'
import {
  Widget,
  WidgetActionItem,
  WidgetContent,
  WidgetFooter,
  WidgetHeader,
  WidgetTitle,
} from '@forto-dev/phoenix-ui-core/ship'

import { useAuth } from '@/features/auth'
import { RequiredActionV2 } from '@/features/workbench'
import { useLocalisation } from '@/utils/localisation'

import { ActionDialog } from './actionDialogNew'

export type Action = {
  id: string
  title: string
  description?: string
  entityID?: string
  state?: 'negative'
  dueDate?: Date
}

export type ActionsWidgetProps = {
  actions: RequiredActionV2[]
  totalActions: number
  viewMoreLink: string
  widgetTitle: string
  hideEntityId?: boolean
}

export const ActionsWidget: FC<ActionsWidgetProps> = ({
  actions,
  totalActions,
  viewMoreLink,
  widgetTitle,
  hideEntityId,
}) => {
  const [selectionAction, setSelectedAction] = useState<RequiredActionV2 | null>(null)
  const { translateDate, translate } = useLocalisation()
  const { isCustomerActive } = useAuth()

  if (!isCustomerActive) {
    return null
  }

  return (
    <>
      {actions.length ? (
        <Widget variant="neutral">
          <WidgetHeader>
            <WidgetTitle>{widgetTitle}</WidgetTitle>
          </WidgetHeader>
          <WidgetContent>
            <Stack gap={8}>
              {actions.map((action) => {
                if (!action.type) {
                  return null
                }

                const tooltipText = translate(`required-actions.action.${action.type}`)

                return (
                  <WidgetActionItem
                    state={action.critical ? ('negative' as const) : undefined}
                    key={action.id}
                    description={hideEntityId ? null : action.entityId}
                    onClick={() => setSelectedAction(action)}
                    title={
                      <Tooltip>
                        <TooltipTrigger>
                          <Typography variant="small-bold" truncate>
                            {tooltipText}
                          </Typography>
                        </TooltipTrigger>
                        <TooltipContent>{tooltipText}</TooltipContent>
                      </Tooltip>
                    }
                  >
                    <Typography variant="small" color="muted" truncate>
                      {action.critical && translate('required-actions.widget.Overdue')}{' '}
                      {action.customerDueDate && translateDate('required_actions_due_date', action.customerDueDate)}
                    </Typography>
                  </WidgetActionItem>
                )
              })}
            </Stack>
          </WidgetContent>
          {totalActions > actions.length && (
            <WidgetFooter>
              <Cluster x="end">
                <PhoenixLink asChild>
                  <Link to={viewMoreLink}>
                    <Typography variant="small">{translate('required-actions.widget.viewMore')}</Typography>{' '}
                    {/* {notAllActionsLoaded && (
                      <Typography variant="small" color="muted">
                        (+{totalActions - actions.length})
                      </Typography>
                    )} */}
                    <Typography variant="small" color="muted">
                      (+{totalActions - actions.length})
                    </Typography>
                  </Link>
                </PhoenixLink>
              </Cluster>
            </WidgetFooter>
          )}
        </Widget>
      ) : null}
      <ActionDialog action={selectionAction} setOpen={setSelectedAction} />
    </>
  )
}
